import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getAuditTrailByid } from "../../../../../redux/auditTrailSlice";
import { getAllVersionHistories } from "../../../../../redux/versionHistorySlice";
import VersionHistoryPopup from "../../../../../common/VersionHistoryPopup/VersionHistoryPopup";
import Loader from "../../../../../common/Loader";
import ProcedureMoveAppBar from "../../../../../common/ProcedureMoveAppBar";
import RenderRevisionNodes from "../../../../Documents/components/RenderRevisionNodes";
import ProcedureStatusInfo from "../../MoveAction/RevisionPreview/ProcedureStatusInfo";
import { processContentData } from "../../utils/previewUtils";
import {
  getSnapshotParseOptionsForAuditRow,
  resolveSnapshot,
  snapshotEnvelopeItemCodeType,
  SNAPSHOT_FETCH_INIT,
} from "../../utils/fetchSnapshotContent";
import {
  buildDisplayTitle,
  buildPreviewCurrentNodeLabelOnly,
  hasMeaningfulPreviewCurrentNodeResolve,
  isTruthyMainEntry,
  parseSnapshotData,
  resolveMovedCopyNode,
} from "../../utils/previewCurrentNodeResolve";
import {
  narrowReviewPreviewContent,
  resolveSnapshotTreeNodeIdForAudit,
} from "../../utils/auditTrailNarrowTree";
import {
  parseAuditSectionCopyMoveLabels,
  parseAuditSectionNumberFromLabel,
  parseAuditSubstepCopyMoveSourceLabels,
  parseAuditSubstepSaveEditLabels,
  parseAuditStepSaveActionLabels,
  parseTargetSectionOrdinalFromAuditTrail,
  parseUserCopiedMovedSectionOrdinal,
} from "../../utils/sectionDisplayOrdinal";
import {
  mergeAuditTrailRowVersionContentDeep,
  parsePartRevFromAuditActionMessage,
} from "../../../utils/auditTrailProcedureIdentity";
import {
  buildItemCatPreviewAppBarTitle,
  resolveItemCatPreviewNode,
} from "../../ItemCatTypeUpdate/itemCatTypePreviewResolve";

/** Normalize axios / RTK thunk body shapes (results vs data.results vs array). */
function pickAuditRowFromGetByIdPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  const row =
    payload.results ??
    payload.data?.results ??
    payload.data?.data ??
    payload.data;
  if (Array.isArray(row)) return row[0] ?? null;
  return row;
}

/** Flatten nested audit version_content so main_entry / preview ids match get-by-id shape. */
function mergeAuditVersionContentFlat(vc) {
  if (!vc || typeof vc !== "object" || Array.isArray(vc)) return {};
  const inner = vc.version_content;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return { ...vc, ...inner };
  }
  return { ...vc };
}

function cloneSnapshotRows(rows) {
  if (!Array.isArray(rows)) return null;
  try {
    return JSON.parse(JSON.stringify(rows));
  } catch {
    return rows.slice();
  }
}

/** Skip null/undefined/empty string and booleans (e.g. `regex.test() && match` yields `false`). */
function pickFirstHeaderValue(...candidates) {
  for (const v of candidates) {
    if (v === undefined || v === null || v === "") continue;
    if (typeof v === "boolean") continue;
    if (typeof v === "string" && !v.trim()) continue;
    return v;
  }
  return undefined;
}

function pickSnapshotProcedureType(obj) {
  if (obj == null || typeof obj !== "object") return undefined;
  return snapshotEnvelopeItemCodeType(obj);
}

/**
 * Snapshot / VC sometimes stores form field labels (e.g. "Section Name*") in section_name — not a real title.
 */
function isJunkAuditBarTitle(value) {
  if (value == null || typeof value !== "string") return false;
  const t = value.trim();
  if (!t) return true;
  if (/^section name\s*\*?$/i.test(t)) return true;
  if (/^short description\s*\*?$/i.test(t)) return true;
  if (/^description\s*\*?$/i.test(t)) return true;
  if (/^name\s*\*?$/i.test(t) && t.length < 12) return true;
  return false;
}

/** Like pickFirstHeaderValue but skips junk UI labels for the Review AppBar title. */
function pickFirstReviewBarTitle(...candidates) {
  for (const v of candidates) {
    if (v === undefined || v === null || v === "") continue;
    if (typeof v === "boolean") continue;
    if (typeof v === "string") {
      const t = v.trim();
      if (!t || isJunkAuditBarTitle(t)) continue;
      return t;
    }
    return v;
  }
  return undefined;
}

function lastBreadcrumbSegment(breadcrumbText) {
  if (typeof breadcrumbText !== "string" || !breadcrumbText.trim()) return null;
  const parts = breadcrumbText.split(" > ").map((p) => p.trim()).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : null;
}

/** First section row from snapshot tree (section_name lives on detail for S3 snapshots). */
function firstSectionTitleFromTree(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const sec = rows[0];
  const named = pickFirstReviewBarTitle(
    sec?.detail?.section_name,
    sec?.version_details?.version_content?.section_name,
    sec?.section_name,
  );
  if (named) return named;
  const ord = parseInt(
    sec?.sibling_order ?? sec?.procedure_tree?.sibling_order ?? "1",
    10,
  );
  if (!Number.isNaN(ord) && ord >= 1) return `Section ${ord}`;
  return "Section 1";
}

/** Last breadcrumb segment when it names a step-level child (Attachment 1, Video 2, …). */
function childNodeLabelFromAuditComment(comment) {
  if (typeof comment !== "string" || !comment.trim()) return null;
  const last = lastBreadcrumbSegment(comment);
  if (!last) return null;
  return /^(Attachment|Video|Canvas|Rich\s+Text)\s+\d+/i.test(last)
    ? last
    : null;
}

/** Human-readable node title for Review AppBar (align with PreviewLayout / RevisionPreview). */
function buildReviewAppBarTitleBase(
  revData,
  selectedView,
  previousMeta,
  newMeta,
  rawSnapshotRows,
) {
  const vcRaw = revData.version_content || {};
  const vc = mergeAuditVersionContentFlat(vcRaw);
  const nestedVc =
    vcRaw.version_content &&
    typeof vcRaw.version_content === "object" &&
    !Array.isArray(vcRaw.version_content)
      ? vcRaw.version_content
      : {};
  const pvc = revData.parent_version_data?.version_content;
  const parentTitle = pvc || {};
  const titleSource = pvc || nestedVc || vc;
  const activeMeta = selectedView === "original" ? previousMeta : newMeta;
  const actionMsg = [
    revData.action,
    revData.action_to_show,
    vc.action_to_show,
    revData.comment,
  ]
    .filter((s) => typeof s === "string" && s.trim())
    .join(" ")
    .trim();

  /** Align verb list with parseAuditStepSaveActionLabels / backend copy ("Create Step N", "Update Step N"). */
  const fromAction =
    actionMsg.match(
      /\b(?:Edit|Save|Updated|Update|Create|Created|User\s+Edited)\s+(Section\s+\d+)/i,
    )?.[1] ||
    actionMsg.match(
      /\b(?:Edit|Save|Updated|Update|Create|Created|User\s+Edited)\s+(Step\s+\d+)/i,
    )?.[1] ||
    actionMsg.match(
      /\b(?:Edit|Save|Updated|Update|Create|Created|User\s+Edited)\s+(Substep\s+[\w\d]+)/i,
    )?.[1] ||
    actionMsg.match(
      /\b(?:Edit|Save|Updated|Update|Create|Created|User\s+Edited)\s+(Attachment\s+\d+)/i,
    )?.[1] ||
    actionMsg.match(
      /\b(?:Edit|Save|Updated|Update|Create|Created|User\s+Edited)\s+(Video\s+\d+)/i,
    )?.[1] ||
    actionMsg.match(
      /\b(?:Edit|Save|Updated|Update|Create|Created|User\s+Edited)\s+(Canvas\s+\d+)/i,
    )?.[1] ||
    actionMsg.match(
      /\b(?:Edit|Save|Updated|Update|Create|Created|User\s+Edited)\s+(Rich\s+Text\s+\d+)/i,
    )?.[1] ||
    /*
     * Bulk_audit_trail titles often start with "User Copied Section …" then "… Copy Canvas 1 into …".
     * Prefer the child node (Canvas / Video / …) and system "Copy Canvas N" before Section so the
     * app bar matches CANVAS_* / VIDEO_* audits, not the section preamble.
     */
    actionMsg.match(/\bUser\s+(?:Moved|Copied|Deleted)\s+(Canvas\s+\d+)\b/i)?.[1] ||
    actionMsg.match(/\bUser\s+(?:Moved|Copied|Deleted)\s+(Video\s+\d+)\b/i)?.[1] ||
    actionMsg.match(/\bUser\s+(?:Moved|Copied|Deleted)\s+(Attachment\s+\d+)\b/i)?.[1] ||
    actionMsg.match(/\bUser\s+(?:Moved|Copied|Deleted)\s+(Rich\s+Text\s+\d+)\b/i)?.[1] ||
    actionMsg.match(/\bCopy\s+(Canvas\s+\d+)\b/i)?.[1] ||
    actionMsg.match(/\bCopy\s+(Video\s+\d+)\b/i)?.[1] ||
    actionMsg.match(/\bCopy\s+(Attachment\s+\d+)\b/i)?.[1] ||
    actionMsg.match(/\bCopy\s+(Rich\s+Text\s+\d+)\b/i)?.[1] ||
    actionMsg.match(
      /\b(?:User\s+)?(?:Moved|Copied|Deleted)\s+(Section\s+\d+)/i,
    )?.[1] ||
    /*
     * New-revision bulk ops: "System Performs Operation 2 of 6, Copy Step 1 into Section 1 in …"
     * Match Copy Step before Copy Section so the bar shows the node being copied, not the target section.
     */
    actionMsg.match(/\bcopy\s+(step\s+\d+)\s+into\s+section\s+\d+/i)?.[1] ||
    actionMsg.match(/\bcopy\s+(step\s+\d+)\b/i)?.[1] ||
    actionMsg.match(/\bcopy\s+(substep\s+[\w\d]+)\s+into\b/i)?.[1] ||
    actionMsg.match(/\bcopy\s+(substep\s+[\w\d]+)\b/i)?.[1] ||
    /* "Copy Section 1 into …" on CREATE_PROCEDURE_REVISION / system copy lines */
    actionMsg.match(/\bCopy\s+(Section\s+\d+)\b/i)?.[1] ||
    actionMsg.match(
      /\b(?:User\s+)?(?:Moved|Copied|Deleted)\s+(Step\s+\d+)/i,
    )?.[1] ||
    actionMsg.match(
      /\b(?:User\s+)?(?:Moved|Copied|Deleted)\s+(Substep\s+[\w\d]+)/i,
    )?.[1] ||
    actionMsg.match(
      /^(?:User\s+)?(?:Moved|Copied)\s+(Attachment\s+\d+)(?:\s+from|\s+into)/i,
    )?.[1] ||
    actionMsg.match(
      /^(?:User\s+)?(?:Moved|Copied)\s+(Video\s+\d+)(?:\s+from|\s+into)/i,
    )?.[1] ||
    actionMsg.match(
      /^(?:User\s+)?(?:Moved|Copied)\s+(Canvas\s+\d+)(?:\s+from|\s+into)/i,
    )?.[1] ||
    actionMsg.match(
      /^(?:User\s+)?(?:Moved|Copied)\s+(Rich\s+Text\s+\d+)(?:\s+from|\s+into)/i,
    )?.[1] ||
    actionMsg.match(/^(?:User\s+)?Deleted\s+(Attachment\s+\d+)/i)?.[1] ||
    actionMsg.match(/^(?:User\s+)?Deleted\s+(Video\s+\d+)/i)?.[1] ||
    actionMsg.match(/^(?:User\s+)?Deleted\s+(Canvas\s+\d+)/i)?.[1] ||
    actionMsg.match(/^(?:User\s+)?Deleted\s+(Rich\s+Text\s+\d+)/i)?.[1];

  const actionShowU = (vc.action_to_show || "").toUpperCase();
  const fromActionChildLoose =
    (/ATTACHMENT/.test(actionShowU)
      ? actionMsg.match(/\b(Attachment\s+\d+)\b/i)?.[1]
      : undefined) ||
    (/VIDEO/.test(actionShowU)
      ? actionMsg.match(/\b(Video\s+\d+)\b/i)?.[1]
      : undefined) ||
    (/CANVAS|CANVA/.test(actionShowU)
      ? actionMsg.match(/\b(Canvas\s+\d+)\b/i)?.[1]
      : undefined) ||
    (/RICH_TEXT|RICH-TEXT/.test(actionShowU)
      ? actionMsg.match(/\b(Rich\s+Text\s+\d+)\b/i)?.[1]
      : undefined);

  const fromMeta = pickFirstReviewBarTitle(
    activeMeta?.pageTitle,
    activeMeta?.section_name,
    typeof activeMeta?.breadcrumb_text === "string"
      ? lastBreadcrumbSegment(activeMeta.breadcrumb_text)
      : null,
  );

  const fromVc = pickFirstReviewBarTitle(
    vc.pageTitle,
    vc.elementTitle,
    vc.name,
    parentTitle.pageTitle,
    parentTitle.elementTitle,
    parentTitle.name,
    titleSource.pageTitle,
    titleSource.elementTitle,
    titleSource.name,
    vc.section_name,
    titleSource.section_name,
    parentTitle.section_name,
    typeof vc.breadcrumb_text === "string"
      ? lastBreadcrumbSegment(vc.breadcrumb_text)
      : null,
    typeof titleSource.breadcrumb_text === "string"
      ? lastBreadcrumbSegment(titleSource.breadcrumb_text)
      : null,
    typeof parentTitle.breadcrumb_text === "string"
      ? lastBreadcrumbSegment(parentTitle.breadcrumb_text)
      : null,
    vc.stepTitle,
    titleSource.stepTitle,
    vc.section,
    titleSource.section,
  );

  const fromComment = childNodeLabelFromAuditComment(revData.comment);
  const fromTree = firstSectionTitleFromTree(rawSnapshotRows);

  return pickFirstReviewBarTitle(
    fromAction,
    fromActionChildLoose,
    fromMeta,
    fromVc,
    fromComment,
    fromTree,
    vc.partNumber,
    titleSource.partNumber,
    vc.type && vc.procedure_number
      ? `${vc.type} ${vc.procedure_number}`.trim()
      : undefined,
    titleSource.type && titleSource.procedure_number
      ? `${titleSource.type} ${titleSource.procedure_number}`.trim()
      : undefined,
    "Procedure",
  );
}

const NODE_CHANGE_REVIEW_PATH = "/audit-trail/reviewcopymovedelete/";

const ReviewLayout = () => {
  const dispatch = useDispatch();
  const { audit_trail_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const bulkReviewFirstOriginalLastNew =
    typeof location.pathname === "string" &&
    location.pathname.includes(NODE_CHANGE_REVIEW_PATH);
  const [revData, setRevData] = useState({ version_content: {} });
  const [selectedView, setSelectedView] = useState("new");
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [previousSnapshotData, setPreviousSnapshotData] = useState({
    data: null,
    meta: null,
    loaded: false,
    error: null,
  });
  const [newSnapshotData, setNewSnapshotData] = useState({
    data: null,
    meta: null,
    loaded: false,
    error: null,
  });
  /** Raw new snapshot JSON — same input as preview-current-node for matching AppBar node + version id. */
  const [rawNewSnapshotForPreviewBar, setRawNewSnapshotForPreviewBar] =
    useState(null);

  // Get version history data from Redux store
  const { versionHistories, loading: isLoadingVersions } = useSelector(
    (state) => state.versionHistory,
  );

  useEffect(() => {
    (async () => {
      try {
        const resp = await dispatch(getAuditTrailByid({ id: audit_trail_id }));
        if (getAuditTrailByid.rejected.match(resp)) {
          throw new Error(
            typeof resp.payload === "string"
              ? resp.payload
              : "Failed to load audit trail",
          );
        }
        const auditData = pickAuditRowFromGetByIdPayload(resp.payload);
        if (!auditData || typeof auditData !== "object") {
          throw new Error("Invalid audit trail response");
        }
        setRevData(auditData);

        const snapshotParseOpts = {
          ...getSnapshotParseOptionsForAuditRow(auditData),
          bulkReviewFirstOriginalLastNew,
        };

        // Previous snapshot: S3 URL, inline array, or { data: [...] } envelope (same as RevisionPreview)
        if (auditData.previous_content_snapshot) {
          try {
            const { rows, meta } = await resolveSnapshot(
              auditData.previous_content_snapshot,
              "original",
              snapshotParseOpts,
            );
            setPreviousSnapshotData({
              data: cloneSnapshotRows(rows),
              meta,
              loaded: true,
              error: null,
            });
          } catch (error) {
            setPreviousSnapshotData({
              data: null,
              meta: null,
              loaded: true,
              error: error.message,
            });
          }
        } else {
          setPreviousSnapshotData({
            data: null,
            meta: null,
            loaded: true,
            error: "No Data",
          });
        }

        if (auditData.new_content_snapshot) {
          try {
            const { rows, meta } = await resolveSnapshot(
              auditData.new_content_snapshot,
              "new",
              snapshotParseOpts,
            );
            setNewSnapshotData({
              data: cloneSnapshotRows(rows),
              meta,
              loaded: true,
              error: null,
            });
          } catch (error) {
            setNewSnapshotData({
              data: null,
              meta: null,
              loaded: true,
              error: error.message,
            });
          }
        } else {
          setNewSnapshotData({
            data: null,
            meta: null,
            loaded: true,
            error: "No Data",
          });
        }
      } catch (error) {
        console.error("Error loading audit trail data:", error);
        setPreviousSnapshotData({
          data: null,
          meta: null,
          loaded: true,
          error: "Failed to load audit trail data",
        });
        setNewSnapshotData({
          data: null,
          meta: null,
          loaded: true,
          error: "Failed to load audit trail data",
        });
      }
    })();
  }, [dispatch, audit_trail_id, bulkReviewFirstOriginalLastNew]);

  useEffect(() => {
    let cancelled = false;
    if (!bulkReviewFirstOriginalLastNew) {
      setRawNewSnapshotForPreviewBar(null);
      return () => {
        cancelled = true;
      };
    }
    const url = revData?.new_content_snapshot;
    if (typeof url !== "string" || !url.trim()) {
      setRawNewSnapshotForPreviewBar(null);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const res = await fetch(url.trim(), SNAPSHOT_FETCH_INIT);
        if (!res.ok || cancelled) return;
        const json = await res.json();
        if (!cancelled) setRawNewSnapshotForPreviewBar(json);
      } catch {
        if (!cancelled) setRawNewSnapshotForPreviewBar(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    bulkReviewFirstOriginalLastNew,
    revData?.new_content_snapshot,
    audit_trail_id,
  ]);

  const previewCurrentNodeResolved = useMemo(() => {
    if (!bulkReviewFirstOriginalLastNew || !rawNewSnapshotForPreviewBar) {
      return null;
    }
    const auditVc = revData.version_content ?? {};
    const actionToShow = auditVc.action_to_show ?? "";
    const previewTreeNodeId = auditVc.preview_tree_node_id;
    const isMainEntry = isTruthyMainEntry(
      auditVc.main_entry ?? auditVc.version_content?.main_entry,
    );
    const snapOpts = getSnapshotParseOptionsForAuditRow(revData);
    const { versionContent, sections } = parseSnapshotData(
      rawNewSnapshotForPreviewBar,
      isMainEntry,
      previewTreeNodeId,
      {
        copyReplaceBulk: snapOpts.copyReplaceBulk,
        auditFirstEntryFlag: snapOpts.auditFirstEntryFlag,
        mainEntryBulk: snapOpts.mainEntryBulk,
      },
    );
    const snapVc =
      versionContent && typeof versionContent === "object" ? versionContent : {};
    const versionContentForResolve = {
      ...snapVc,
      action_to_show: [
        auditVc.action_to_show,
        auditVc.version_content?.action_to_show,
        revData.action_to_show,
        revData.action,
        snapVc.action_to_show,
      ]
        .filter((s) => typeof s === "string" && s.trim())
        .join(" ")
        .trim() || String(snapVc.action_to_show ?? ""),
    };
    return resolveMovedCopyNode(
      actionToShow,
      sections,
      previewTreeNodeId,
      rawNewSnapshotForPreviewBar,
      versionContentForResolve,
    );
  }, [bulkReviewFirstOriginalLastNew, rawNewSnapshotForPreviewBar, revData]);

  // Pick the correct snapshot based on selected view (same as move-copy-delete preview)
  const currentSnapshotData =
    selectedView === "original" ? previousSnapshotData : newSnapshotData;
  const isContentLoaded = currentSnapshotData.loaded;
  const rawContentData = currentSnapshotData.data;
  const processedData = processContentData(
    Array.isArray(rawContentData) ? rawContentData : [],
  );

  const vc = revData.version_content || {};
  const vcFlat = mergeAuditVersionContentFlat(vc);
  const nestedVc = vc.version_content;
  /** Second-level envelope: version_content.version_content.{current_status,…} */
  const nestedVcFlat =
    nestedVc && typeof nestedVc === "object" && !Array.isArray(nestedVc)
      ? mergeAuditVersionContentFlat(nestedVc)
      : {};
  const pvc = revData.parent_version_data?.version_content;
  const pvcFlat = mergeAuditVersionContentFlat(pvc || {});
  /** Parent + row VC (nested envelopes); leaf VIDEO/CANVAS rows often omit top-level part/rev. */
  const mergedDeepAuditVc = mergeAuditTrailRowVersionContentDeep(revData) || {};
  const pr = revData.procedure_revision || {};
  const previousMeta = previousSnapshotData.meta;
  const newMeta = newSnapshotData.meta;
  const auditActionTextForPartRev = [
    revData.action,
    revData.action_to_show,
    vcFlat.action_to_show,
    mergedDeepAuditVc.action_to_show,
    revData.comment,
  ]
    .filter((s) => typeof s === "string" && s.trim())
    .join(" ")
    .trim();
  const parsedPartRev =
    parsePartRevFromAuditActionMessage(auditActionTextForPartRev);

  const previewContentData = narrowReviewPreviewContent(
    processedData,
    revData,
    selectedView,
    previousMeta,
    newMeta,
  );
  const hasContent = previewContentData && previewContentData.length > 0;

  /**
   * View Original: previous S3 / inline snapshot meta must come *before* version_content,
   * which usually describes the *after* state — otherwise status/revision never reflect "before".
   * If the previous snapshot has no procedure fields, fall through to new snapshot meta + audit row.
   */
  const displayStatus =
    selectedView === "original"
      ? pickFirstHeaderValue(
          mergedDeepAuditVc.current_status,
          mergedDeepAuditVc.status,
          mergedDeepAuditVc.status_changed_to,
          vcFlat.current_status,
          vcFlat.status,
          vcFlat.status_changed_to,
          nestedVcFlat.current_status,
          nestedVcFlat.status,
          nestedVcFlat.status_changed_to,
          pvcFlat.current_status,
          pvcFlat.status,
          pvcFlat.status_changed_to,
          pvc?.current_status,
          pvc?.status,
          previousMeta?.status,
          pr.current_status,
          pr.status,
          nestedVc?.current_status,
          nestedVc?.status,
          vc.current_status,
          vc.status,
          vc.status_changed_to,
          newMeta?.status,
          revData.status,
        )
      : pickFirstHeaderValue(
          mergedDeepAuditVc.current_status,
          mergedDeepAuditVc.status,
          mergedDeepAuditVc.status_changed_to,
          vcFlat.current_status,
          vcFlat.status,
          vcFlat.status_changed_to,
          nestedVcFlat.current_status,
          nestedVcFlat.status,
          nestedVcFlat.status_changed_to,
          pvcFlat.current_status,
          pvcFlat.status,
          pvcFlat.status_changed_to,
          pvc?.current_status,
          pvc?.status,
          nestedVc?.current_status,
          nestedVc?.status,
          vc.current_status,
          vc.status,
          vc.status_changed_to,
          pr.current_status,
          pr.status,
          newMeta?.status,
          previousMeta?.status,
          revData.status,
        );

  const displayRevision =
    selectedView === "original"
      ? pickFirstHeaderValue(
          mergedDeepAuditVc.rev,
          mergedDeepAuditVc.revision,
          mergedDeepAuditVc.part_rev,
          mergedDeepAuditVc.to_part_rev,
          pvcFlat.rev,
          pvcFlat.revision,
          pvcFlat.part_rev,
          pvc?.rev,
          pvc?.revision,
          previousMeta?.revision ?? previousMeta?.rev,
          pr.revision,
          pr.rev,
          pr.label,
          nestedVc?.rev,
          nestedVc?.revision,
          vc.rev,
          vc.revision,
          vc.part_rev,
          revData.rev,
          revData.revision,
          newMeta?.revision ?? newMeta?.rev,
          parsedPartRev.revision,
        )
      : pickFirstHeaderValue(
          mergedDeepAuditVc.rev,
          mergedDeepAuditVc.revision,
          mergedDeepAuditVc.part_rev,
          mergedDeepAuditVc.to_part_rev,
          pvcFlat.rev,
          pvcFlat.revision,
          pvcFlat.part_rev,
          pvc?.rev,
          pvc?.revision,
          nestedVc?.rev,
          nestedVc?.revision,
          vc.rev,
          vc.revision,
          vc.part_rev,
          pr.revision,
          pr.rev,
          pr.label,
          revData.rev,
          revData.revision,
          newMeta?.revision ?? newMeta?.rev,
          previousMeta?.revision ?? previousMeta?.rev,
          parsedPartRev.revision,
        );

  const displayItemCodeType =
    selectedView === "original"
      ? pickFirstHeaderValue(
          pickSnapshotProcedureType(mergedDeepAuditVc),
          pickSnapshotProcedureType(previousMeta),
          pickSnapshotProcedureType(newMeta),
          pickSnapshotProcedureType(pvcFlat),
          pickSnapshotProcedureType(pvc),
          pickSnapshotProcedureType(pr),
          pickSnapshotProcedureType(nestedVc),
          pickSnapshotProcedureType(vc),
        )
      : pickFirstHeaderValue(
          pickSnapshotProcedureType(mergedDeepAuditVc),
          pickSnapshotProcedureType(newMeta),
          pickSnapshotProcedureType(previousMeta),
          pickSnapshotProcedureType(pvcFlat),
          pickSnapshotProcedureType(pvc),
          pickSnapshotProcedureType(nestedVc),
          pickSnapshotProcedureType(vc),
          pickSnapshotProcedureType(pr),
        );

  const displayPartNumber =
    selectedView === "original"
      ? pickFirstHeaderValue(
          mergedDeepAuditVc.partNumber,
          mergedDeepAuditVc.part_number,
          mergedDeepAuditVc.procedure_number,
          pvcFlat.partNumber,
          pvcFlat.part_number,
          pvcFlat.procedure_number,
          pvc?.partNumber,
          pvc?.part_number,
          previousMeta?.partNumber ?? previousMeta?.part_number,
          pr.part_number,
          pr.partNumber,
          nestedVc?.partNumber,
          nestedVc?.part_number,
          vc.partNumber,
          vc.part_number,
          revData.partNumber,
          revData.part_number,
          newMeta?.partNumber ?? newMeta?.part_number,
          parsedPartRev.partNumber,
        )
      : pickFirstHeaderValue(
          mergedDeepAuditVc.partNumber,
          mergedDeepAuditVc.part_number,
          mergedDeepAuditVc.procedure_number,
          pvcFlat.partNumber,
          pvcFlat.part_number,
          pvcFlat.procedure_number,
          pvc?.partNumber,
          pvc?.part_number,
          nestedVc?.partNumber,
          nestedVc?.part_number,
          vc.partNumber,
          vc.part_number,
          pr.part_number,
          pr.partNumber,
          revData.partNumber,
          revData.part_number,
          newMeta?.partNumber ?? newMeta?.part_number,
          previousMeta?.partNumber ?? previousMeta?.part_number,
          parsedPartRev.partNumber,
        );

  /** Match Documents version history: prepend part + rev when API rows lack breadcrumb_text. */
  const versionHistoryBreadcrumbFallback = useMemo(() => {
    const code =
      displayPartNumber != null ? String(displayPartNumber).trim() : "";
    const r = displayRevision;
    const revPart =
      r != null && String(r).trim() !== ""
        ? `Rev ${String(r).trim()}`
        : "";
    return [code, revPart].filter(Boolean).join(" > ");
  }, [displayPartNumber, displayRevision]);

  const activeMetaForTitle =
    selectedView === "original" ? previousMeta : newMeta;

  /** Match `/audit-trail/item_type_cat_update_preview/…` AppBar for UPDATE_STEP / UPDATE_CANVAS full-tree rows. */
  const auditSnapshotOptsForItemCatBar =
    getSnapshotParseOptionsForAuditRow(revData);
  const useItemCatStyleReviewAppBar =
    auditSnapshotOptsForItemCatBar.auditUpdateStepProcedureTree === true ||
    auditSnapshotOptsForItemCatBar.auditLeafEditProcedureTree === true;
  const itemCatPreviewTreeNodeIdForBar = useMemo(
    () =>
      pickFirstHeaderValue(
        vcFlat.preview_tree_node_id,
        newMeta?.tree_node_id,
      ),
    [vcFlat.preview_tree_node_id, newMeta?.tree_node_id],
  );
  const itemCatResolvedForReviewBar = useMemo(() => {
    if (!useItemCatStyleReviewAppBar) return null;
    if (
      !newSnapshotData.loaded ||
      !Array.isArray(newSnapshotData.data) ||
      newSnapshotData.data.length === 0
    ) {
      return null;
    }
    const tid = itemCatPreviewTreeNodeIdForBar;
    if (!tid) return null;
    return resolveItemCatPreviewNode(
      newSnapshotData.data,
      String(tid).trim(),
    );
  }, [
    useItemCatStyleReviewAppBar,
    newSnapshotData.loaded,
    newSnapshotData.data,
    itemCatPreviewTreeNodeIdForBar,
  ]);
  const itemCatReviewAppBarTitle = useMemo(() => {
    if (!useItemCatStyleReviewAppBar || !revData.version_id) return null;
    if (itemCatResolvedForReviewBar == null) return null;
    return buildItemCatPreviewAppBarTitle(
      revData.version_id,
      itemCatResolvedForReviewBar,
    );
  }, [
    useItemCatStyleReviewAppBar,
    revData.version_id,
    itemCatResolvedForReviewBar,
  ]);

  /**
   * On copy/move/delete review, do not feed loaded snapshot rows into the regex title: when S3 content
   * arrives (~few seconds), `firstSectionTitleFromTree` / meta would otherwise replace a stable label
   * with a different section/procedure name.
   */
  const rawRowsForRegexReviewTitle = bulkReviewFirstOriginalLastNew
    ? []
    : Array.isArray(rawContentData)
      ? rawContentData
      : [];
  const regexDisplayTitleBase = buildReviewAppBarTitleBase(
    revData,
    selectedView,
    previousMeta,
    newMeta,
    rawRowsForRegexReviewTitle,
  );
  const displayTitleBase =
    bulkReviewFirstOriginalLastNew &&
    rawNewSnapshotForPreviewBar &&
    previewCurrentNodeResolved != null &&
    hasMeaningfulPreviewCurrentNodeResolve(previewCurrentNodeResolved)
      ? buildPreviewCurrentNodeLabelOnly(previewCurrentNodeResolved)
      : regexDisplayTitleBase;
  /**
   * Procedure / leaf id for AppBar "(Version …)" — align with PreviewLayout: prefer snapshot meta and
   * audit preview_tree_node_id / entity refs over revData.version_id (often substep/step version UUID).
   */
  const versionIdForBar = pickFirstHeaderValue(
    activeMetaForTitle?.tree_node_id,
    vcFlat.preview_tree_node_id,
    nestedVcFlat.preview_tree_node_id,
    pvcFlat.preview_tree_node_id,
    vcFlat.canvas_tree_node_id,
    vcFlat.canva_tree_node_id,
    vcFlat.rich_text_tree_node_id,
    vcFlat.rich_text_node_id,
    vcFlat.video_tree_node_id,
    vcFlat.attachment_tree_node_id,
    vcFlat.step_tree_node_id,
    vcFlat.sub_step_tree_node_id,
    vcFlat.substep_tree_node_id,
    vcFlat.tree_node_id,
    vcFlat.procedure_tree_ref,
    vcFlat.version_id_ref,
    revData.version_id,
  );
  // AppBar must show audit `version_id` from get-by-id API, not tree_node_id / preview_tree_node_id.
  const versionIdShown = String(revData.version_id || "");
  /** Same `buildDisplayTitle` as `/audit-trail/preview-current-node` (label + `(Version ${audit.version_id}) Selected`). */
  const previewCurrentNodeAppBarTitle =
    bulkReviewFirstOriginalLastNew &&
    revData.version_id &&
    previewCurrentNodeResolved != null &&
    hasMeaningfulPreviewCurrentNodeResolve(previewCurrentNodeResolved)
      ? buildDisplayTitle(revData.version_id, previewCurrentNodeResolved)
      : null;
  const displayTitle =
    itemCatReviewAppBarTitle && itemCatReviewAppBarTitle !== "Loading..."
      ? itemCatReviewAppBarTitle
      : previewCurrentNodeAppBarTitle &&
          previewCurrentNodeAppBarTitle !== "Loading..."
        ? previewCurrentNodeAppBarTitle
        : revData.version_id
          ? `${displayTitleBase} (Version ${versionIdShown}) Review`
          : "Loading...";

  const actionMessage = [revData.action, revData.action_to_show, vcFlat.action_to_show]
    .filter((s) => typeof s === "string" && s.trim())
    .join(" ")
    .trim();
  const auditSubstepCopyLabels =
    parseAuditSubstepCopyMoveSourceLabels(actionMessage);
  const auditSubstepSaveLabels = parseAuditSubstepSaveEditLabels(
    actionMessage,
    revData.comment,
  );
  const auditStepSaveLabels = parseAuditStepSaveActionLabels(
    actionMessage,
    revData.comment,
  );
  const auditSectionCopyLabels =
    parseAuditSectionCopyMoveLabels(actionMessage);
  const titleBaseTrim =
    typeof displayTitleBase === "string" ? displayTitleBase.trim() : "";
  const titleDerivedSectionLabel = (() => {
    const m = titleBaseTrim.match(/^Section\s+(\d+)\b/i);
    if (!m) return null;
    const n = parseInt(m[1], 10);
    return !Number.isNaN(n) && n >= 1 ? `Section ${n}` : null;
  })();
  const titleDerivedStepOrdinal = (() => {
    const m = titleBaseTrim.match(/^Step\s+(\d+)\b/i);
    if (!m) return null;
    const n = parseInt(m[1], 10);
    return !Number.isNaN(n) && n >= 1 ? n : null;
  })();
  const titleDerivedSubstepHeading = (() => {
    const m = titleBaseTrim.match(/^(Substep\s+[\dA-Za-z]+)\b/i);
    return m ? m[1] : null;
  })();
  const titleDerivedSubstepStepOrdinal = (() => {
    const m = titleBaseTrim.match(/^Substep\s+(\d+)/i);
    if (!m) return null;
    const n = parseInt(m[1], 10);
    return !Number.isNaN(n) && n >= 1 ? n : null;
  })();
  const reviewAuditSubstepHeading =
    auditSubstepCopyLabels?.substepHeading ??
    auditSubstepSaveLabels?.substepHeading ??
    titleDerivedSubstepHeading ??
    null;
  const reviewStepOrdinalOverride =
    auditSubstepCopyLabels?.sourceStepOrdinal ??
    auditSubstepSaveLabels?.stepOrdinal ??
    titleDerivedSubstepStepOrdinal ??
    auditStepSaveLabels?.stepOrdinal ??
    titleDerivedStepOrdinal ??
    null;
  const auditSectionLabelForReview =
    auditSubstepCopyLabels?.sourceSectionLabel ??
    auditSubstepSaveLabels?.sectionLabel ??
    auditStepSaveLabels?.sectionLabel ??
    auditSectionCopyLabels?.sectionLabel ??
    titleDerivedSectionLabel ??
    null;
  const auditSectionOrdinalFromAction = (() => {
    const fromMsg = parseUserCopiedMovedSectionOrdinal(actionMessage);
    if (fromMsg != null) return fromMsg;
    const c = revData.comment;
    if (typeof c === "string" && c.trim()) {
      const fromC = parseUserCopiedMovedSectionOrdinal(c);
      if (fromC != null) return fromC;
    }
    const pvcFlat = mergeAuditVersionContentFlat(pvc);
    return parseTargetSectionOrdinalFromAuditTrail({
      action: [revData.action, revData.action_to_show].filter(Boolean).join(" ").trim(),
      action_to_show: vcFlat.action_to_show,
      pageTitle: vcFlat.pageTitle,
      breadcrumb_text: pickFirstHeaderValue(
        vcFlat.breadcrumb_text,
        revData.breadcrumb_text,
        pvcFlat.breadcrumb_text,
      ),
      comment: revData.comment,
    });
  })();
  const auditSectionLabelResolved =
    auditSectionLabelForReview ??
    (auditSectionOrdinalFromAction != null
      ? `Section ${auditSectionOrdinalFromAction}`
      : null);
  const snapshotFocusTid = resolveSnapshotTreeNodeIdForAudit(
    revData,
    selectedView,
    previousMeta,
    newMeta,
  );
  const auditNeedsTreeFocus =
    reviewStepOrdinalOverride != null || reviewAuditSubstepHeading != null;
  const auditFocusStepTreeNodeId =
    auditNeedsTreeFocus
      ? pickFirstHeaderValue(
          snapshotFocusTid,
          vcFlat.sub_step_tree_node_id,
          vcFlat.substep_tree_node_id,
          vcFlat.step_tree_node_id,
          vcFlat.preview_tree_node_id,
          vcFlat.element_id,
          vcFlat.procedure_tree_ref,
          vcFlat.tree_node_id,
        )
      : null;
  const auditOrdinalFallbackSectionNumber = (() => {
    const n = parseAuditSectionNumberFromLabel(auditSectionLabelResolved);
    return n != null ? n : 1;
  })();

  const vcFlatForSectionFocus = mergeAuditVersionContentFlat(
    revData.version_content,
  );
  const pvcFlatForSectionFocus = mergeAuditVersionContentFlat(
    revData.parent_version_data?.version_content,
  );
  const auditFocusSectionTreeNodeId =
    parseAuditSectionNumberFromLabel(auditSectionLabelResolved) != null
      ? pickFirstHeaderValue(
          vcFlatForSectionFocus.preview_tree_node_id,
          vcFlatForSectionFocus.procedure_tree_ref,
          vcFlatForSectionFocus.tree_node_id,
          vcFlatForSectionFocus.section_tree_node_id,
          pvcFlatForSectionFocus.preview_tree_node_id,
          pvcFlatForSectionFocus.procedure_tree_ref,
        )
      : undefined;

  const handleReviewAgainstDifferentVersion = async () => {
    await dispatch(getAllVersionHistories({ versionId: revData.version_id }));
    setShowVersionHistory(true);
  };

  const { loading } = useSelector((state) => state.audittrail);

  return (
    <div style={{ paddingBottom: "10px" }}>
      <ProcedureMoveAppBar
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        showBackButton={false}
        showCancelButton={true}
        title={displayTitle}
        titleFontSize="18px"
        buttons={[
          {
            label: isLoadingVersions
              ? "Loading..."
              : "Review Against Different Version",
            onClick: handleReviewAgainstDifferentVersion,
          },
        ]}
      />

      {loading ? (
        <Loader />
      ) : (
        <>
          <ProcedureStatusInfo
            item_code_type={displayItemCodeType}
            partNumber={displayPartNumber}
            status={displayStatus}
            revision={displayRevision}
            partDescription={
              revData.parent_version_data?.version_content?.partDescription ??
              newMeta?.partDescription ??
              previousMeta?.partDescription ??
              vc?.version_content?.partDescription ??
              vc.partDescription
            }
            minimalBoldHeader={true}
            partNumberMarginLeft={true}
          />

          {!isContentLoaded ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <div>
                Loading {selectedView === "original" ? "original" : "new"}{" "}
                content...
              </div>
            </div>
          ) : currentSnapshotData.error ? (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color:
                  currentSnapshotData.error === "No Data"
                    ? "inherit"
                    : "#dc3545",
              }}
            >
              <div>{currentSnapshotData.error}</div>
            </div>
          ) : hasContent ? (
            <div style={{ padding: "20px" }}>
              <RenderRevisionNodes
                key={`${audit_trail_id}-${selectedView}`}
                preloadedData={previewContentData}
                showCheckbox={false}
                disabled={true}
                forceShowChildHeadings={true}
              />
            </div>
          ) : (
            <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
              <div>No Data</div>
            </div>
          )}
        </>
      )}

      {showVersionHistory && (
        <VersionHistoryPopup
          isOpen={showVersionHistory}
          onClose={() => setShowVersionHistory(false)}
          versionHistories={versionHistories || []}
          mode="edit"
          versionId={revData.version_id}
          nodeId={
            revData.version_content?.element_id ||
            revData.version_content?.step_tree_node_id
          }
          nodeIdField="element_id"
          breadcrumbText={
            versionHistoryBreadcrumbFallback ||
            String(revData.version_content?.breadcrumb_text || "").trim()
          }
          revisionTreeNodes={processedData}
          title="Select Version for Comparison"
          headerStyle={{ color: "#0D6EFD" }}
          initialFilterType="all"
          currentVersionId={revData.version_id}
          onViewDetails={(selectedVersion) => {
            setShowVersionHistory(false);
            navigate(
              `/audit-trail/comparison/${audit_trail_id}?compareWith=${selectedVersion.version_id}`,
            );
          }}
        />
      )}
    </div>
  );
};

export default ReviewLayout;
